import React, { useState, useEffect } from 'react';
import { Zap, Clock, Filter, TrendingUp, Flame, Users, ArrowDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import FlashDealCard from '../components/shared/FlashDealCard';
import { flashDealsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

export default function LiveNowPage() {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('ending_soon');
  const [liveViewers, setLiveViewers] = useState(342);

  useEffect(() => {
    const fetchDeals = async () => {
      try {
        const res = await flashDealsAPI.getAll(true);
        setDeals(res.data);
      } catch (error) {
        console.error('Error fetching deals:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchDeals();
    
    // Poll for new deals every 30 seconds
    const interval = setInterval(fetchDeals, 30000);
    
    // Simulate live viewer count
    const viewerInterval = setInterval(() => {
      setLiveViewers(prev => prev + Math.floor(Math.random() * 10) - 4);
    }, 2000);

    return () => {
      clearInterval(interval);
      clearInterval(viewerInterval);
    };
  }, []);

  const sortedDeals = [...deals].sort((a, b) => {
    if (sortBy === 'ending_soon') {
      return new Date(a.end_time) - new Date(b.end_time);
    }
    if (sortBy === 'highest_discount') {
      return b.discount_percent - a.discount_percent;
    }
    if (sortBy === 'lowest_price') {
      return a.flash_price - b.flash_price;
    }
    return 0;
  });

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <section className="bg-white border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl flex items-center justify-center">
                <Zap className="text-white" size={28} />
              </div>
              <div>
                <h1 className="font-display font-bold text-2xl md:text-3xl text-zinc-900">Live Deals</h1>
                <p className="text-zinc-500">Real-time flash deals ending soon</p>
              </div>
            </div>
            
            {/* Live Stats */}
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2 px-4 py-2 bg-zinc-100 rounded-full">
                <span className="live-dot" />
                <span className="text-sm font-semibold text-zinc-900">{deals.length} active deals</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 bg-zinc-100 rounded-full">
                <Users size={14} className="text-zinc-500" />
                <span className="text-sm font-semibold text-zinc-900">{liveViewers} watching</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-white border-b border-zinc-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 py-3 flex flex-wrap gap-3 items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-sm text-zinc-500">Sort by:</span>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[180px] bg-zinc-50 border-zinc-200 rounded-full" data-testid="sort-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ending_soon">Ending Soon</SelectItem>
                <SelectItem value="highest_discount">Highest Discount</SelectItem>
                <SelectItem value="lowest_price">Lowest Price</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex items-center gap-2 text-sm text-zinc-500">
            <TrendingUp size={14} />
            <span>Updated live</span>
          </div>
        </div>
      </section>

      {/* Deals Grid */}
      <section className="max-w-7xl mx-auto px-4 py-8">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="aspect-square bg-white rounded-2xl skeleton" />
            ))}
          </div>
        ) : sortedDeals.length > 0 ? (
          <motion.div 
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
            initial="hidden"
            animate="visible"
            variants={{
              visible: { transition: { staggerChildren: 0.05 } }
            }}
          >
            <AnimatePresence>
              {sortedDeals.map((deal, idx) => (
                <motion.div
                  key={deal.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ delay: idx * 0.05 }}
                >
                  <FlashDealCard deal={deal} featured={idx === 0} />
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        ) : (
          <div className="text-center py-20 bg-white rounded-2xl border border-zinc-200">
            <div className="w-20 h-20 mx-auto mb-4 bg-zinc-100 rounded-2xl flex items-center justify-center">
              <Clock className="text-zinc-400" size={40} />
            </div>
            <h3 className="font-display text-xl text-zinc-900 mb-2">No Live Deals</h3>
            <p className="text-zinc-500 max-w-md mx-auto">
              All flash deals have ended. Check back soon or browse the marketplace.
            </p>
          </div>
        )}
      </section>
    </div>
  );
}
